import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { BookOpen, Loader2, Plus, Trash2 } from "lucide-react";

// PROMPT 88 (2026-09-01): ficha comercial del tour, en español e inglés. Es lo que el
// agente de WhatsApp lee para responder "qué incluye", "dónde nos encontramos", "cuánto
// dura", "qué llevo", accesibilidad y preguntas frecuentes. Se guarda aparte del resto
// del tour (PUT /tours/{id}/ficha) para no tocar horarios ni modalidades.

const CAMPOS = [
  { key: "descripcion", label: "Descripción corta", rows: 3, placeholder: "Qué es la excursión, en dos o tres frases." },
  { key: "duracion", label: "Duración", rows: 1, placeholder: "Ej: Todo el día, unas 14 horas." },
  { key: "incluye", label: "Qué incluye", rows: 2, placeholder: "Transporte, guía en español, ..." },
  { key: "no_incluye", label: "Qué no incluye", rows: 2, placeholder: "Comidas, entradas opcionales, ..." },
  { key: "que_llevar", label: "Qué llevar", rows: 2, placeholder: "Documento, ropa cómoda, ..." },
  { key: "accesibilidad", label: "Accesibilidad", rows: 2, placeholder: "Silla de ruedas, cochecitos, mascotas, ..." },
  { key: "punto_encuentro", label: "Punto de encuentro", rows: 1, placeholder: "Si se deja vacío, se usa el punto de recogida del tour." },
];

const fichaVacia = () => ({
  descripcion: "", duracion: "", incluye: "", no_incluye: "", que_llevar: "", accesibilidad: "", punto_encuentro: "", faq: [],
});

function normalizar(f) {
  const base = fichaVacia();
  const out = { ...base, ...(f || {}) };
  out.faq = Array.isArray(out.faq) ? out.faq.map((x) => ({ pregunta: x?.pregunta || "", respuesta: x?.respuesta || "" })) : [];
  return out;
}

function EditorIdioma({ valor, onChange, testPrefix }) {
  const set = (patch) => onChange({ ...valor, ...patch });
  const setFaq = (i, patch) => {
    const faq = valor.faq.map((x, j) => (j === i ? { ...x, ...patch } : x));
    set({ faq });
  };
  return (
    <div className="space-y-3">
      {CAMPOS.map((c) => (
        <div key={c.key} className="space-y-1.5">
          <Label className="text-xs">{c.label}</Label>
          {c.rows > 1 ? (
            <Textarea rows={c.rows} value={valor[c.key] || ""} onChange={(e) => set({ [c.key]: e.target.value })} placeholder={c.placeholder} data-testid={`${testPrefix}-${c.key}`} />
          ) : (
            <Input value={valor[c.key] || ""} onChange={(e) => set({ [c.key]: e.target.value })} placeholder={c.placeholder} data-testid={`${testPrefix}-${c.key}`} />
          )}
        </div>
      ))}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-xs">Preguntas frecuentes</Label>
          <Button type="button" variant="outline" size="sm" onClick={() => set({ faq: [...valor.faq, { pregunta: "", respuesta: "" }] })} data-testid={`${testPrefix}-faq-add`} className="gap-1.5 h-7 text-xs">
            <Plus className="h-3.5 w-3.5" /> Agregar
          </Button>
        </div>
        {valor.faq.length === 0 && <p className="text-xs text-slate-400">Sin preguntas cargadas.</p>}
        {valor.faq.map((item, i) => (
          <div key={i} className="rounded-md border border-slate-200 p-2 space-y-1.5">
            <div className="flex gap-2">
              <Input value={item.pregunta} onChange={(e) => setFaq(i, { pregunta: e.target.value })} placeholder="Pregunta" data-testid={`${testPrefix}-faq-p-${i}`} />
              <Button type="button" variant="ghost" size="icon" onClick={() => set({ faq: valor.faq.filter((_, j) => j !== i) })} className="h-9 w-9 text-slate-400 hover:text-red-600 shrink-0" data-testid={`${testPrefix}-faq-del-${i}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            <Textarea rows={2} value={item.respuesta} onChange={(e) => setFaq(i, { respuesta: e.target.value })} placeholder="Respuesta" data-testid={`${testPrefix}-faq-r-${i}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function FichaComercialDialog({ open, onOpenChange, tour, onSaved }) {
  const [es, setEs] = useState(fichaVacia());
  const [en, setEn] = useState(fichaVacia());
  const [alias, setAlias] = useState(""); // PROMPT 90: otros nombres, uno por linea
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setEs(normalizar(tour?.ficha_comercial?.es));
      setEn(normalizar(tour?.ficha_comercial?.en));
      setAlias((tour?.ficha_comercial?.alias || []).join("\n"));
    }
  }, [open, tour]);

  const limpiar = (f) => ({
    ...f,
    faq: f.faq.filter((x) => (x.pregunta || "").trim() || (x.respuesta || "").trim()),
  });

  const guardar = async () => {
    if (!tour?.id) return;
    setSaving(true);
    try {
      await api.put(`/tours/${tour.id}/ficha`, {
        es: limpiar(es),
        en: limpiar(en),
        alias: alias.split("\n").map((a) => a.trim()).filter(Boolean),
      });
      toast.success("Ficha comercial guardada.");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ficha-comercial-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <BookOpen className="h-5 w-5" /> Ficha comercial · {tour?.nombre}
          </DialogTitle>
          <DialogDescription>
            Lo que el agente de WhatsApp le cuenta al cliente sobre este tour. Los horarios, cupos y precios NO van acá: los toma
            del catálogo y de las tarifas.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label className="text-xs">Otros nombres con los que lo conocen los clientes (uno por línea)</Label>
          <Textarea rows={2} value={alias} onChange={(e) => setAlias(e.target.value)} placeholder={"Manhattan de Norte a Sur"} data-testid="ficha-alias" />
          <p className="text-[11px] text-slate-500">El agente entiende que estos nombres son este mismo tour. Los horarios, cupos y precios siguen saliendo del catálogo.</p>
        </div>
        <Tabs defaultValue="es">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="es" data-testid="ficha-tab-es">Español</TabsTrigger>
            <TabsTrigger value="en" data-testid="ficha-tab-en">English</TabsTrigger>
          </TabsList>
          <TabsContent value="es" className="mt-4">
            <EditorIdioma valor={es} onChange={setEs} testPrefix="ficha-es" />
          </TabsContent>
          <TabsContent value="en" className="mt-4">
            <p className="text-xs text-slate-500 mb-3">Si se deja vacío, el agente usa la versión en español y responde en inglés igual.</p>
            <EditorIdioma valor={en} onChange={setEn} testPrefix="ficha-en" />
          </TabsContent>
        </Tabs>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="ficha-cancelar">Cancelar</Button>
          <Button onClick={guardar} disabled={saving} data-testid="ficha-guardar">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar ficha"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
